import CheckCircle from '@mui/icons-material/CheckCircle'
import {
  Box,
  Button,
  CircularProgress,
  Typography,
} from '@mui/material'
import DriversLicensePreview, {
  DRIVER_LICENSE_FILE_ACCEPT,
  isDriverLicensePdf,
} from '@/components/DriversLicensePreview'
import { PrivateUserInfoType } from '@otw/models'
import { useClientDriversLicenseAdmin } from 'hooks/admin/useClientDriversLicenseAdmin'
import { useSnackbar } from '@/hooks/common/useSnackbar'
import React from 'react'

interface ClientDriversLicenseSectionProps {
  clientUser: PrivateUserInfoType | null
  canEdit?: boolean
  /** Shown when `clientUser` is null */
  emptyMessage?: string
  /** Hide the "Stored on client account" line (e.g. in user management) */
  hideClientSummary?: boolean
}

const ClientDriversLicenseSection: React.FC<ClientDriversLicenseSectionProps> = ({
  clientUser,
  canEdit = false,
  emptyMessage,
  hideClientSummary = false,
}) => {
  const { showSuccess, showError } = useSnackbar()
  const {
    hasLicense,
    previewUrl,
    isUploading,
    isLoadingUrl,
    error,
    uploadLicense,
    removeLicense,
  } = useClientDriversLicenseAdmin(
    clientUser?.id,
    clientUser?.driver_license_front_storage_path,
  )

  if (!clientUser) {
    return (
      <Typography variant='body2' color='text.secondary'>
        {emptyMessage ??
          'Link a portal client (matching email, phone, or name) to view or upload a driver\'s license.'}
      </Typography>
    )
  }

  const handleUpload = async (file?: File) => {
    if (!file) return
    try {
      await uploadLicense(file)
      showSuccess("Driver's license uploaded successfully")
    } catch {
      showError("Failed to upload driver's license. Please try again.")
    }
  }

  const handleRemove = async () => {
    try {
      await removeLicense()
      showSuccess("Driver's license removed")
    } catch {
      showError("Failed to remove driver's license. Please try again.")
    }
  }

  const hasPreview = hasLicense && !!previewUrl
  const previewIsPdf = isDriverLicensePdf(
    previewUrl ?? clientUser.driver_license_front_storage_path,
  )

  return (
    <Box>
      {!hideClientSummary && (
        <Typography variant='body2' color='text.secondary' sx={{ mb: 2 }}>
          Stored on client account: {clientUser.first_name} {clientUser.last_name} ({clientUser.email})
        </Typography>
      )}

      {isLoadingUrl ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
          <CircularProgress size={28} />
        </Box>
      ) : hasPreview && previewUrl ? (
        <Box position='relative' mb={2}>
          <DriversLicensePreview url={previewUrl} isPdf={previewIsPdf} maxHeight={280} />
          <Box
            sx={{
              position: 'absolute',
              top: 8,
              right: 8,
              color: 'success.main',
              bgcolor: 'background.paper',
              borderRadius: '50%',
              p: 0.25,
              lineHeight: 0,
            }}
          >
            <CheckCircle fontSize='small' />
          </Box>
        </Box>
      ) : (
        <Box
          sx={{
            width: '100%',
            height: 120,
            borderRadius: 1,
            bgcolor: 'grey.100',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            mb: 2,
            border: '1px dashed',
            borderColor: 'divider',
          }}
        >
          <Typography variant='body2' color='text.secondary'>
            No photo or PDF uploaded yet
          </Typography>
        </Box>
      )}

      <Box display='flex' flexWrap='wrap' gap={1}>
        {canEdit && (
          <Button variant='contained' component='label' disabled={isUploading || isLoadingUrl} size='small'>
            {isUploading ? 'Uploading...' : hasLicense ? 'Replace file' : 'Upload photo or PDF'}
            <input
              hidden
              type='file'
              accept={DRIVER_LICENSE_FILE_ACCEPT}
              onChange={e => {
                const file = e.target.files?.[0]
                void handleUpload(file)
                e.currentTarget.value = ''
              }}
            />
          </Button>
        )}
        {canEdit && hasLicense && (
          <Button
            variant='outlined'
            color='error'
            size='small'
            disabled={isUploading || isLoadingUrl}
            onClick={() => void handleRemove()}
          >
            Remove
          </Button>
        )}
        {hasPreview && (
          <Button
            variant='outlined'
            size='small'
            disabled={isUploading}
            onClick={() => window.open(previewUrl, '_blank', 'noopener,noreferrer')}
          >
            {previewIsPdf ? 'Open PDF' : 'View full size'}
          </Button>
        )}
      </Box>

      {(error || (hasLicense && !previewUrl && !isLoadingUrl)) && (
        <Typography variant='caption' color='error' sx={{ display: 'block', mt: 1 }}>
          {error ?? 'Unable to load preview.'}
        </Typography>
      )}
    </Box>
  )
}

export default ClientDriversLicenseSection
